import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axiosClient from "../api/axiosClient";
import {
  Card, DataTable, Btn, Alert, StatusBadge, StandardModal,
  colors, inputStyle, selectStyle, textareaStyle,
} from "../design-system";

function Field({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
      <div style={{ fontSize: 14, marginTop: 2 }}>{value ?? "—"}</div>
    </div>
  );
}

const emptyEditForm = {
  first_name: "", last_name: "", email: "", job_title: "",
  teaching_qualification: "", assessing_qualification: "",
  occupational_sector: "", years_experience: "", notes: "",
};

const emptyCpdForm = {
  activity_date: new Date().toISOString().slice(0, 10),
  activity_type: "self_directed", description: "", hours: "",
  is_sector_cpd: true, evidence_description: "",
};

const ACTIVITY_TYPES = [
  "formal_qualification", "short_course", "conference", "self_directed",
  "workplace_practice", "delivery_of_cpd", "professional_body", "reflection",
];

export default function TrainerAssessorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [person, setPerson] = useState(null);
  const [cpd, setCpd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [msgType, setMsgType] = useState("success");

  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [saving, setSaving] = useState(false);

  const [approveModal, setApproveModal] = useState(false);
  const [approveYears, setApproveYears] = useState(3);
  const [approveNotes, setApproveNotes] = useState("");

  const [suspendModal, setSuspendModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");

  const [revokeModal, setRevokeModal] = useState(false);
  const [revokeReason, setRevokeReason] = useState("");

  const [cpdModal, setCpdModal] = useState(false);
  const [cpdForm, setCpdForm] = useState(emptyCpdForm);
  const [savingCpd, setSavingCpd] = useState(false);

  const [busy, setBusy] = useState(false);

  const flash = (m, t = "success") => { setMsg(m); setMsgType(t); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        axiosClient.get(`/trainer-assessors/${id}`),
        axiosClient.get(`/trainer-assessors/${id}/cpd`),
      ]);
      setPerson(pRes.data);
      setCpd(cRes.data);
    } catch (e) {
      flash(e.userMessage || "Failed to load record", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  function openEdit() {
    setEditForm({
      first_name: person.first_name || "", last_name: person.last_name || "",
      email: person.email || "", job_title: person.job_title || "",
      teaching_qualification: person.teaching_qualification || "",
      assessing_qualification: person.assessing_qualification || "",
      occupational_sector: person.occupational_sector || "",
      years_experience: person.years_experience ?? "",
      notes: person.notes || "",
    });
    setEditModal(true);
  }

  async function handleEdit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...editForm };
      payload.years_experience = payload.years_experience === "" ? null : parseInt(payload.years_experience, 10);
      const res = await axiosClient.patch(`/trainer-assessors/${id}`, payload);
      setPerson(res.data);
      flash("Details updated.");
      setEditModal(false);
    } catch (e) {
      flash(e.userMessage || "Failed to update", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleApprove(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axiosClient.post(`/trainer-assessors/${id}/approve`, {
        approval_years: parseInt(approveYears, 10), notes: approveNotes || undefined,
      });
      setPerson(res.data);
      flash("Approved.");
      setApproveModal(false);
      setApproveNotes("");
    } catch (e) {
      flash(e.userMessage || "Failed to approve", "error");
    } finally {
      setBusy(false);
    }
  }

  async function handleSuspend(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axiosClient.post(`/trainer-assessors/${id}/suspend`, { reason: suspendReason });
      setPerson(res.data);
      flash("Suspended.");
      setSuspendModal(false);
      setSuspendReason("");
    } catch (e) {
      flash(e.userMessage || "Failed to suspend", "error");
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axiosClient.post(`/trainer-assessors/${id}/revoke`, { reason: revokeReason });
      setPerson(res.data);
      flash("Revoked.");
      setRevokeModal(false);
      setRevokeReason("");
    } catch (e) {
      flash(e.userMessage || "Failed to revoke", "error");
    } finally {
      setBusy(false);
    }
  }

  async function handleSubmitReapproval() {
    setBusy(true);
    try {
      const res = await axiosClient.post(`/trainer-assessors/${id}/submit-reapproval`);
      setPerson(res.data);
      flash("Re-approval submission recorded.");
    } catch (e) {
      flash(e.userMessage || "Failed to record re-approval submission", "error");
    } finally {
      setBusy(false);
    }
  }

  async function handleLogCpd(e) {
    e.preventDefault();
    setSavingCpd(true);
    try {
      await axiosClient.post(`/trainer-assessors/${id}/cpd`, {
        ...cpdForm, hours: parseInt(cpdForm.hours, 10),
      });
      flash("CPD activity logged.");
      setCpdModal(false);
      setCpdForm(emptyCpdForm);
      load();
    } catch (e) {
      flash(e.userMessage || "Failed to log CPD activity", "error");
    } finally {
      setSavingCpd(false);
    }
  }

  if (loading && !person) return <p style={{ color: colors.textMuted }}>Loading…</p>;
  if (!person) return <p style={{ color: colors.danger }}>Record not found.</p>;

  const cpdColumns = [
    { key: "activity_date", label: "Date", render: (_, r) => new Date(r.activity_date).toLocaleDateString("en-GB") },
    { key: "activity_type", label: "Type", render: (_, r) => r.activity_type.replace(/_/g, " ") },
    { key: "description", label: "Description" },
    { key: "hours", label: "Hours" },
    { key: "verified", label: "Verified", render: (_, r) => <StatusBadge status={r.verified ? "approved" : "pending"} customLabel={r.verified ? "Verified" : "Unverified"} size="sm" /> },
  ];

  const canApprove = ["pending", "suspended", "lapsed", "reapproval_due"].includes(person.status);
  const canSuspend = person.status === "approved";
  const canRevoke = person.status !== "revoked";
  const canSubmitReapproval = person.status === "reapproval_due";

  return (
    <div>
      <Link to="/accreditation/trainer-assessors" style={{ fontSize: 13, color: colors.primary, textDecoration: "none" }}>&larr; Back to Trainers &amp; Assessors</Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", margin: "12px 0 24px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <h1 style={{ fontSize: 26, fontWeight: 700, color: colors.textPrimary, margin: 0 }}>{person.full_name}</h1>
            <StatusBadge status={person.status} />
          </div>
          <p style={{ color: colors.textMuted, margin: "4px 0 0", textTransform: "capitalize" }}>{person.person_type} · {person.job_title || "—"}</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <Btn variant="secondary" size="sm" onClick={openEdit}>Edit</Btn>
          {canApprove && <Btn variant="success" size="sm" onClick={() => setApproveModal(true)}>Approve</Btn>}
          {canSuspend && <Btn variant="warning" size="sm" onClick={() => setSuspendModal(true)}>Suspend</Btn>}
          {canRevoke && <Btn variant="danger" size="sm" onClick={() => setRevokeModal(true)}>Revoke</Btn>}
          {canSubmitReapproval && <Btn variant="primary" size="sm" onClick={handleSubmitReapproval} disabled={busy}>Mark Re-approval Received</Btn>}
        </div>
      </div>

      {msg && <Alert type={msgType} message={msg} style={{ marginBottom: 16 }} />}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
        <Card padding={24}>
          <h3 style={{ fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Profile</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Field label="Email" value={person.email} />
            <Field label="Occupational Sector" value={person.occupational_sector} />
            <Field label="Teaching Qualification" value={person.teaching_qualification} />
            <Field label="Assessing Qualification" value={person.assessing_qualification} />
            <Field label="Years Experience" value={person.years_experience} />
            <Field label="Provider Company ID" value={person.provider_company_id} />
          </div>
          {person.notes && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.textMuted, textTransform: "uppercase" }}>Notes</div>
              <div style={{ fontSize: 13, whiteSpace: "pre-wrap", marginTop: 4 }}>{person.notes}</div>
            </div>
          )}
        </Card>

        <Card padding={24}>
          <h3 style={{ fontWeight: 600, marginBottom: 16, fontSize: 15 }}>Approval Lifecycle</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            <Field label="Approved" value={person.approved_at ? new Date(person.approved_at).toLocaleDateString("en-GB") : "—"} />
            <Field label="Expires" value={person.approval_expiry ? new Date(person.approval_expiry).toLocaleDateString("en-GB") : "—"} />
            <Field label="Days to Expiry" value={person.days_to_expiry} />
            <Field label="Re-approval Notice Sent" value={person.reapproval_notice_sent_at ? new Date(person.reapproval_notice_sent_at).toLocaleDateString("en-GB") : "—"} />
            <Field label="Re-approval Submitted" value={person.reapproval_submitted_at ? new Date(person.reapproval_submitted_at).toLocaleDateString("en-GB") : "—"} />
            <Field label="Same-Provider Streak" value={person.consecutive_same_provider_count} />
          </div>
        </Card>
      </div>

      <Card padding={24}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div>
            <h3 style={{ fontWeight: 600, fontSize: 15, margin: 0 }}>CPD Activity — {cpd?.year}</h3>
            <p style={{ color: colors.textMuted, fontSize: 13, margin: "4px 0 0" }}>
              {cpd?.completed || 0} / {cpd?.target || 0} hours logged
            </p>
          </div>
          <Btn size="sm" variant="primary" onClick={() => setCpdModal(true)}>+ Log Activity</Btn>
        </div>
        {cpd?.target > 0 && (
          <div style={{ background: colors.backgroundAlt, borderRadius: 999, height: 8, marginBottom: 16, overflow: "hidden" }}>
            <div style={{
              width: `${Math.min(100, (cpd.completed / cpd.target) * 100)}%`, height: "100%",
              background: cpd.completed >= cpd.target ? colors.success : colors.primary,
            }} />
          </div>
        )}
        <DataTable columns={cpdColumns} rows={cpd?.records || []} keyField="id" loading={loading} emptyMessage="No CPD activity logged for this year." />
      </Card>

      {editModal && (
        <StandardModal
          title="Edit Details"
          onClose={() => setEditModal(false)}
          width={640}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setEditModal(false)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="edit-ta-form" disabled={saving}>{saving ? "Saving…" : "Save"}</Btn>
            </div>
          }
        >
          <form id="edit-ta-form" onSubmit={handleEdit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>First Name</label>
                <input value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Last Name</label>
                <input value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Email</label>
                <input type="email" value={editForm.email} onChange={e => setEditForm(f => ({ ...f, email: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Job Title</label>
                <input value={editForm.job_title} onChange={e => setEditForm(f => ({ ...f, job_title: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Teaching Qualification</label>
                <input value={editForm.teaching_qualification} onChange={e => setEditForm(f => ({ ...f, teaching_qualification: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Assessing Qualification</label>
                <input value={editForm.assessing_qualification} onChange={e => setEditForm(f => ({ ...f, assessing_qualification: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Occupational Sector</label>
                <input value={editForm.occupational_sector} onChange={e => setEditForm(f => ({ ...f, occupational_sector: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Years Experience</label>
                <input type="number" min="0" value={editForm.years_experience} onChange={e => setEditForm(f => ({ ...f, years_experience: e.target.value }))} style={inputStyle} />
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Notes</label>
              <textarea rows={3} value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} style={textareaStyle} />
            </div>
          </form>
        </StandardModal>
      )}

      {approveModal && (
        <StandardModal
          title={`Approve ${person.full_name}`}
          onClose={() => setApproveModal(false)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setApproveModal(false)}>Cancel</Btn>
              <Btn variant="success" type="submit" form="approve-form" disabled={busy}>{busy ? "Approving…" : "Approve"}</Btn>
            </div>
          }
        >
          <form id="approve-form" onSubmit={handleApprove}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Approval Period (years)</label>
            <select value={approveYears} onChange={e => setApproveYears(e.target.value)} style={selectStyle}>
              <option value={1}>1 year</option>
              <option value={2}>2 years</option>
              <option value={3}>3 years</option>
            </select>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", margin: "12px 0 4px" }}>Notes</label>
            <textarea rows={3} value={approveNotes} onChange={e => setApproveNotes(e.target.value)} style={textareaStyle} />
          </form>
        </StandardModal>
      )}

      {suspendModal && (
        <StandardModal
          title={`Suspend ${person.full_name}`}
          onClose={() => setSuspendModal(false)}
          danger
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setSuspendModal(false)}>Cancel</Btn>
              <Btn variant="warning" type="submit" form="suspend-form" disabled={busy || !suspendReason}>{busy ? "Suspending…" : "Suspend"}</Btn>
            </div>
          }
        >
          <form id="suspend-form" onSubmit={handleSuspend}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Reason *</label>
            <textarea required rows={3} value={suspendReason} onChange={e => setSuspendReason(e.target.value)} style={textareaStyle} />
          </form>
        </StandardModal>
      )}

      {revokeModal && (
        <StandardModal
          title={`Revoke ${person.full_name}`}
          onClose={() => setRevokeModal(false)}
          danger
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setRevokeModal(false)}>Cancel</Btn>
              <Btn variant="danger" type="submit" form="revoke-form" disabled={busy || !revokeReason}>{busy ? "Revoking…" : "Revoke"}</Btn>
            </div>
          }
        >
          <form id="revoke-form" onSubmit={handleRevoke}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Reason *</label>
            <textarea required rows={3} value={revokeReason} onChange={e => setRevokeReason(e.target.value)} style={textareaStyle} />
          </form>
        </StandardModal>
      )}

      {cpdModal && (
        <StandardModal
          title="Log CPD Activity"
          onClose={() => setCpdModal(false)}
          footer={
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="secondary" onClick={() => setCpdModal(false)}>Cancel</Btn>
              <Btn variant="primary" type="submit" form="cpd-form" disabled={savingCpd}>{savingCpd ? "Logging…" : "Log Activity"}</Btn>
            </div>
          }
        >
          <form id="cpd-form" onSubmit={handleLogCpd}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Date *</label>
                <input required type="date" value={cpdForm.activity_date} onChange={e => setCpdForm(f => ({ ...f, activity_date: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Hours *</label>
                <input required type="number" min="1" value={cpdForm.hours} onChange={e => setCpdForm(f => ({ ...f, hours: e.target.value }))} style={inputStyle} />
              </div>
              <div style={{ gridColumn: "1 / -1" }}>
                <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Activity Type *</label>
                <select required value={cpdForm.activity_type} onChange={e => setCpdForm(f => ({ ...f, activity_type: e.target.value }))} style={selectStyle}>
                  {ACTIVITY_TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </div>
            </div>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", margin: "12px 0 4px" }}>Description *</label>
            <textarea required rows={3} value={cpdForm.description} onChange={e => setCpdForm(f => ({ ...f, description: e.target.value }))} style={textareaStyle} />
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, marginTop: 12, cursor: "pointer" }}>
              <input type="checkbox" checked={cpdForm.is_sector_cpd} onChange={e => setCpdForm(f => ({ ...f, is_sector_cpd: e.target.checked }))} />
              Counts as occupational sector CPD
            </label>
          </form>
        </StandardModal>
      )}
    </div>
  );
}
